import { useState, useRef, useEffect } from "react";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import AddEmployeePage from "../../onboarding/Addemployeepage";
import CurrentEmployment from "../../onboarding/currentEmployment";
import ShiftMapping from "../../onboarding/shiftMapping";
import PreviousEmployment from "../../onboarding/PreviousEmployment";
import EducationRecords from "../../onboarding/educationRecords";
import EmployeeDocuments from "../../onboarding/EmployeeDocuments";
import AccountDetails from "../../onboarding/accountDetails";
import EmergencyInfo from "../../onboarding/EmergencyInfo";
import Assess from "../../onboarding/access/assess";
import PositionHistory from "../../onboarding/positionHistory";
import sessionDB from "../../../utils/sessionDB";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User, Check, AlertCircle } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader } from "../../../components/ui";

const ROW_1_SECTIONS = [
  { id: "employee-info", labelKey: "onboarding.employeeInformation" },
  { id: "current-employee", labelKey: "common.currentEmployment" },
  { id: "shift-mappings", labelKey: "onboarding.shiftMappings" },
  { id: "emergency-info", labelKey: "onboarding.emergencyInformation" },
  { id: "employee-docs", labelKey: "onboarding.employeeDocuments" }
];

const ROW_2_SECTIONS = [
  { id: "position-history", labelKey: "onboarding.positionHistory" },
  { id: "previous-employment", labelKey: "pagination.previousEmployment" },
  { id: "education-records", labelKey: "onboarding.educationRecords" },
  { id: "asset-allocation", labelKey: "onboarding.assetAccessAllocation" },
  { id: "account-details", labelKey: "onboarding.accountDetails" }
];

const ONBOARDING_SECTIONS = [...ROW_1_SECTIONS, ...ROW_2_SECTIONS];

function getSectionLabel(tab) {
  return i18n.t(tab.labelKey);
}

export function EmployeeOnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  const [activeSection, setActiveSection] = useState("employee-info");
  const [employeeId, setEmployeeId] = useState(editId || null);
  const [toast, setToast] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);

  // Dynamic refs for each section so components can register their submit handlers independently
  const employeeInfoRef = useRef({});
  const currentEmployeeRef = useRef({});
  const shiftMappingsRef = useRef({});
  const emergencyInfoRef = useRef({});
  const employeeDocsRef = useRef({});
  const positionHistoryRef = useRef({});
  const assetAllocationRef = useRef({});
  const previousEmploymentRef = useRef({});
  const educationRecordsRef = useRef({});
  const accountDetailsRef = useRef({});

  const saveTriggers = {
    "employee-info": employeeInfoRef,
    "current-employee": currentEmployeeRef,
    "shift-mappings": shiftMappingsRef,
    "emergency-info": emergencyInfoRef,
    "employee-docs": employeeDocsRef,
    "position-history": positionHistoryRef,
    "asset-allocation": assetAllocationRef,
    "previous-employment": previousEmploymentRef,
    "education-records": educationRecordsRef,
    "account-details": accountDetailsRef
  };

  const continueAfterSaveRef = useRef(false);

  // Clear previous onboarding employee ID and cached drafts from session when starting a new onboarding flow
  useEffect(() => {
    if (!isEditMode) {
      setEmployeeId(null);
      sessionDB.updateSession({
        onboardingEmployeeId: null,
        onboardingCurrentEmploymentDraft: null,
        onboardingPositionHistoryDraft: null
      });
    } else {
      setEmployeeId(editId);
    }
  }, [isEditMode, editId]);

  const handleSuccess = (data) => {
    const empId = data?.employeeId || data?.empId || data?.data?.employeeId || data?.data?.empId;
    if (empId) {
      setEmployeeId(String(empId));
      sessionDB.updateSession({ onboardingEmployeeId: String(empId) });
    }
    const currentIdx = ONBOARDING_SECTIONS.findIndex(s => s.id === activeSection);
    const isLastSection = currentIdx >= ONBOARDING_SECTIONS.length - 1;

    // "Continue" button: silently advance to the next tab.
    if (continueAfterSaveRef.current) {
      continueAfterSaveRef.current = false;
      if (!isLastSection) {
        setActiveSection(ONBOARDING_SECTIONS[currentIdx + 1].id);
      }
      return;
    }

    setToast(i18n.t("onboarding.savedSuccessfully"));
    if (isLastSection) {
      setTimeout(() => {
        setToast(null);
        navigate("/hrms-team/onboarding/employees");
      }, 1200);
    } else {
      setTimeout(() => setToast(null), 1200);
      setActiveSection(ONBOARDING_SECTIONS[currentIdx + 1].id);
    }
  };

  const handleSave = () => {
    continueAfterSaveRef.current = false;
    const activeRef = saveTriggers[activeSection];
    if (activeRef && activeRef.current && typeof activeRef.current.submit === "function") {
      activeRef.current.submit();
    }
  };

  const handleContinue = () => {
    if (activeSection === "employee-info" && !employeeId) {
      continueAfterSaveRef.current = true;
      if (employeeInfoRef.current && typeof employeeInfoRef.current.submit === "function") {
        employeeInfoRef.current.submit();
      }
    } else {
      const currentIdx = ONBOARDING_SECTIONS.findIndex(s => s.id === activeSection);
      if (currentIdx < ONBOARDING_SECTIONS.length - 1) {
        setActiveSection(ONBOARDING_SECTIONS[currentIdx + 1].id);
      }
    }
  };

  const handleRateLimitError = () => {
    setRateLimitError(i18n.t("onboarding.errors.tooManyRequestsTryAgainLater"));
  };

  const handleCancel = () => {
    navigate("/hrms-team/onboarding/employees");
  };

  const renderTabButton = (tab) => {
    const isActive = activeSection === tab.id;
    const isDisabled = tab.id !== "employee-info" && !employeeId;
    return (
      <button
        key={tab.id}
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && setActiveSection(tab.id)}
        className={`flex items-center justify-center text-center px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition w-full min-w-0 ${
          isActive
            ? "bg-[var(--color-primary)] text-white shadow-[0_4px_12px_rgba(30,51,131,0.2)]"
            : isDisabled
            ? "opacity-50 cursor-not-allowed text-[var(--color-text-secondary)]/50 bg-white/40"
            : "bg-white/70 hover:bg-white text-[var(--color-text-secondary)] hover:text-foreground border border-[var(--color-text-border)]/30 hover:border-[var(--color-text-border)]/60"
        }`}
      >
        <span className="truncate">{getSectionLabel(tab)}</span>
      </button>
    );
  };

  return (
    <AppLayout
      overlay={
        toast ? (
          <div className="fixed bottom-5 right-5 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300 pointer-events-none">
            <div className="flex items-center gap-2.5 bg-white border border-[var(--color-text-border)]/60 text-foreground px-4 py-3 rounded-2xl shadow-[0_15px_40px_-10px_rgba(30,51,131,0.25)] font-sans text-sm font-medium">
              <div className="h-6 w-6 rounded-full bg-[var(--color-success)]/10 text-[var(--color-success)] grid place-items-center">
                <Check className="h-4 w-4" />
              </div>
              <span>{toast}</span>
            </div>
          </div>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Fixed Pinned Top Header & Navigation Container */}
        <div className="sticky top-[-24px] lg:top-[-28px] bg-[var(--color-workspace)] z-20 pt-2 pb-3 border-b border-[var(--color-text-border)]/50 space-y-3">
          {/* Navigation / Header */}
          <div className="flex flex-col gap-1">
            <PageBackLink to="/hrms-team/onboarding/employees" />
            
            <PageHeader
              title={isEditMode ? i18n.t("onboarding.editEmployeeProfile") : i18n.t("onboarding.onboardNewEmployee")}
            />

            {rateLimitError ? (
              <div
                className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{rateLimitError}</span>
              </div>
            ) : null}
          </div>

          {/* Two-Row Navigation Tabs */}
          <div className="flex flex-col gap-2 pt-1">
            {/* First Row: Employee Information to Employee Documents */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 w-full">
              {ROW_1_SECTIONS.map((tab) => renderTabButton(tab))}
            </div>
            {/* Second Row: Position History to Account Details */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 w-full">
              {ROW_2_SECTIONS.map((tab) => renderTabButton(tab))}
            </div>
          </div>
        </div>

        {/* Wizard Form content (Keep all sections mounted to preserve input states) */}
        <div className="bg-white rounded-2xl border border-white/60 p-6 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] space-y-6">
          {ONBOARDING_SECTIONS.map((tab) => {
            const isCurrent = activeSection === tab.id;
            const sectionLabel = getSectionLabel(tab);
            return (
              <div
                key={tab.id}
                className={isCurrent ? "space-y-4 min-h-[300px] flex flex-col justify-between" : "hidden"}
              >
                <div className="space-y-2">
                  <h3 className="text-base font-bold text-foreground">{sectionLabel}</h3>
                  {tab.id === "employee-info" ? (
                    <AddEmployeePage
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      onRateLimitError={handleRateLimitError}
                      saveTriggerRef={employeeInfoRef}
                    />
                  ) : tab.id === "current-employee" ? (
                    <CurrentEmployment
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={currentEmployeeRef}
                    />
                  ) : tab.id === "shift-mappings" ? (
                    <ShiftMapping
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={shiftMappingsRef}
                    />
                  ) : tab.id === "emergency-info" ? (
                    <EmergencyInfo
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={emergencyInfoRef}
                    />
                  ) : tab.id === "employee-docs" ? (
                    <EmployeeDocuments
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={employeeDocsRef}
                    />
                  ) : tab.id === "position-history" ? (
                    <PositionHistory
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={positionHistoryRef}
                    />
                  ) : tab.id === "asset-allocation" ? (
                    <Assess
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={assetAllocationRef}
                      initialEmployeeId={employeeId}
                    />
                  ) : tab.id === "previous-employment" ? (
                    <PreviousEmployment
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={previousEmploymentRef}
                    />
                  ) : tab.id === "education-records" ? (
                    <EducationRecords
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={educationRecordsRef}
                    />
                  ) : tab.id === "account-details" ? (
                    <AccountDetails
                      accessToken={sessionDB.getAccessToken()}
                      tenantCode={sessionDB.getTenantCode()}
                      initialEmployeeId={employeeId}
                      onBack={handleCancel}
                      onNext={handleContinue}
                      onSuccess={handleSuccess}
                      isTabMode={true}
                      isActive={isCurrent}
                      saveTriggerRef={accountDetailsRef}
                    />
                  ) : (
                    <div className="p-8 border border-dashed border-[var(--color-text-border)] rounded-2xl bg-[var(--color-workspace)]/10 text-center flex flex-col items-center justify-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] grid place-items-center">
                        <User className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {sectionLabel} {i18n.t("onboarding.formComponentSuffix")}
                        </p>
                        <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                          <Trans i18nKey="serviceExplorer.formInputsValidationLogicAndBackendApiHandlers" />
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Single Shared Navigation Footer */}
          <div className="flex justify-between items-center pt-6 border-t border-[var(--color-text-border)]/35 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="h-10 px-5 rounded-xl border text-xs sm:text-sm font-semibold bg-white text-[var(--color-text-secondary)] hover:text-foreground hover:bg-[var(--color-workspace)]"
            >
              <Trans i18nKey="formActions.cancel" />
            </Button>
            
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleSave}
                className="h-10 px-5 rounded-xl border border-[var(--color-primary)]/35 text-[var(--color-primary)] bg-white hover:bg-slate-50 text-xs sm:text-sm font-semibold"
              >
                <Trans i18nKey="formActions.save" />
              </Button>
              
              {activeSection !== "account-details" && (
                <Button
                  type="button"
                  onClick={handleContinue}
                  className="h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] text-xs sm:text-sm font-semibold"
                >
                  <Trans i18nKey="formActions.continue" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
